import type { GameCode } from '../../entities/room/types';
import { apiFetch, authHeader, jsonBody } from '../../shared/api/httpClient';
import { clientHeaders } from '../../shared/lib/device';
import type { BigWinGame } from '../../pages/tabs/bigWinsView';

export type LeaderboardPeriod = '30m' | '1h' | '3h' | '12h' | '24h' | 'today' | '7d' | '30d';
export type LeaderboardGame = 'all' | GameCode | BigWinGame;
// "profit" ranks the player; "wins" ranks individual big wins across Milhar and
// Draw. "copiers" ranked what the people who copied a player actually earned —
// it has NO entry point in the app any more (the tab that showed it now shows
// "wins"), and nothing in this repo sends it. It stays in the union only
// because the backend still accepts it; deleting copiersBoard() there means
// touching the board whitelist and its contract tests, which is a real change
// with real risk and no user-visible payoff. Scheduled for a separate cleanup
// once "wins" has run in production for a while — see BIG_WINS_BOARD_DESIGN §11.
export type LeaderboardBoard = 'profit' | 'copiers' | 'wins';
export type LeaderboardSort = 'profit' | 'roi' | 'streak';

// Every entrance that advertises the board must speak in the same window, or
// the Hall promises a leader the page then fails to show.
export const DEFAULT_LEADERBOARD_PERIOD: LeaderboardPeriod = '1h';

// The bet a player can be copied on: enough to rebuild the pick in the room's
// own copy sheet without a second round-trip.
export type LeaderboardLastBet = {
  betId?: number | string;
  roomCode?: string;
  game?: GameCode;
  tab?: string;
  pick?: string;
  amount?: number;
  roundNumber?: number;
  placedAt?: number;
};

export type LeaderboardUser = {
  id?: number | string;
  user_id?: number | string;
  userId?: number | string;
  player_id?: string;
  playerId?: string;
  username?: string;
  name?: string;
  initial?: string;
  flag?: string;
  level?: number;
  vipLevel?: number;
  vip_level?: number;
  avatar_url?: string;
  avatarUrl?: string;
  avatar_default_id?: number;
  avatarDefaultId?: number;
  public_leaderboard?: boolean;
  publicLeaderboard?: boolean;
  isAdmin?: boolean;
  is_admin?: boolean;
};

export type LeaderboardItem = {
  rank?: number;
  // False for the viewer's own row when it sits below the sample threshold —
  // it rides along so "your rank" can render, but it is not part of the board.
  ranked?: boolean;
  user?: LeaderboardUser;
  plays?: number;
  wins?: number;
  winRate?: number;
  payout?: number;
  profit?: number;
  roi?: number;
  streak?: number;
  online?: boolean;
  roomCode?: string;
  lastBet?: LeaderboardLastBet | null;
  // Non-empty only when the player is sitting in the same room their last bet
  // was placed in — copying anywhere else would place a bet they never made.
  copyRoomCode?: string;
  isMe?: boolean;
  // board=wins only. See doc/BIG_WINS_BOARD_DESIGN.md §8.
  game?: string;
  tier?: string | null;
  roundNumber?: number | null;
  // The server's label uses the country's registered timezone, not the
  // viewer's. The client re-renders every row off `roundStartsAt` in the
  // device zone instead — see bigWinRoundLabel — and only falls back to this
  // string when that timestamp is missing or the game is unrecognized.
  roundLabel?: string;
  // The instant this round or draw happened, for every row type: Milhar's
  // round window START (rounds.cutoff_ts) or Draw's raffle_event.scheduled_at.
  // The client formats it in the device timezone — Milhar renumbers the
  // round, Draw renders an HH:mm clock — see bigWinRoundLabel.
  roundStartsAt?: number | null;
  eventId?: string | null;
  stake?: number | null;
  multiple?: number | null;
  // 对上几位，以及这一笔是不是**头奖**（对满位数）。判定在后端，前端只渲染。
  //
  // 不要试图从 multiple 反推：线上那笔真头奖是 ×2734,8，而同一张榜上的 ×2000 是
  // 每小时档固定赔率的最高档、根本不是头奖，派彩（R$ 10.000）还比它大。
  // 「金额最大的那一行」和「头奖」是两回事。
  matched?: number | null;
  isJackpot?: boolean;
  wonAt?: number;
};

export type LeaderboardResponse = {
  currency?: string;
  period?: LeaderboardPeriod;
  game?: LeaderboardGame;
  room?: string;
  board?: LeaderboardBoard;
  sort?: LeaderboardSort;
  onlineOnly?: boolean;
  minPlays?: number;
  since?: number;
  items?: LeaderboardItem[];
  me?: LeaderboardItem | null;
  // Window-wide totals, NOT a sum of the returned page — see the backend TODO §5.
  summary?: { count?: number; totalPayout?: number };
  // 最近一次头奖，**不受 period 限制**。头奖大约一个月一次，30 天窗口里经常
  // 一条都没有，而「这游戏到底有没有人真中过大奖」正是旁观者最想问的问题
  // （BIG_WINS_BOARD_DESIGN §5.6）。从来没出过头奖时后端返回 null。
  //
  // `user` 为 null 表示中奖者关掉了公开榜 —— 这一笔仍然要显示，只是不出名字。
  // 整行滤掉会让卡片说「上一次头奖是 60 天前」，而事实是昨天刚出过一次。
  lastJackpot?: {
    user?: LeaderboardUser | null;
    game?: string;
    tier?: string | null;
    roundNumber?: number;
    roundStartsAt?: number;
    stake?: number;
    payout?: number;
    wonAt?: number;
  } | null;
};

// Mirrors the board's windows so the profile can answer in the same lens the
// list was filtered by, plus the long "is this a real player" 7-day view.
export type PublicProfilePeriod = '30m' | '1h' | '3h' | 'today' | '7d';

// Where the player is sitting right now, and the bet you could copy them on.
export type PublicProfilePresence = {
  online?: boolean;
  room_code?: string;
  roomCode?: string;
  last_bet?: LeaderboardLastBet | null;
  lastBet?: LeaderboardLastBet | null;
  copy_room_code?: string;
  copyRoomCode?: string;
};

export type PublicProfileStats = {
  period?: PublicProfilePeriod;
  plays?: number;
  wins?: number;
  win_count?: number;
  win_rate?: number;
  winRate?: number;
  profit_brl?: number;
  profitBrl?: number;
  profit_usdt?: number;
  profitUsdt?: number;
};

export type PublicProfileBet = {
  id?: string | number;
  round_number?: number;
  roundNumber?: number;
  round?: number;
  game?: GameCode;
  tab?: string;
  pick?: string;
  amount?: number;
  currency?: string;
  payout?: number;
  status?: string;
  placed_at?: number;
};

// Lifetime raffle presence — public by design (winner/entry lists already show
// usernames), so it renders regardless of the stats_visible preference.
export type PublicProfileRaffle = {
  entered?: number;
  wins?: number;
  last_win_amount?: number;
  last_win_at?: number;
};

export type PublicProfileResponse = {
  user?: LeaderboardUser;
  stats_visible?: boolean;
  statsVisible?: boolean;
  stats?: PublicProfileStats;
  recent_bets?: PublicProfileBet[];
  recentBets?: PublicProfileBet[];
  raffle?: PublicProfileRaffle;
  followers_count?: number;
  followersCount?: number;
  is_following?: boolean;
  isFollowing?: boolean;
  is_self?: boolean;
  isSelf?: boolean;
  presence?: PublicProfilePresence;
};

export type FollowResponse = {
  ok?: boolean;
  userId?: number | string;
  following?: boolean;
  followersCount?: number;
};

export type SocialFollowUser = {
  id?: number | string;
  username?: string;
  vipLevel?: number;
  avatarDefaultId?: number | null;
  avatarThumbUrl?: string;
};

export type SocialFollowRow = {
  user?: SocialFollowUser;
  followedAt?: number;
  // followers list only: whether I already follow this person back.
  followingBack?: boolean;
  // following list only: live position, so the list can be acted on.
  online?: boolean;
  roomCode?: string;
  lastBet?: LeaderboardLastBet | null;
  copyRoomCode?: string;
};

export function fetchSocialFollowing(token: string) {
  return apiFetch<{ items?: SocialFollowRow[]; count?: number; onlineCount?: number }>('/api/social/following', {
    cache: 'no-store',
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
}

export function fetchSocialFollowers(token: string) {
  return apiFetch<{ items?: SocialFollowRow[]; count?: number }>('/api/social/followers', {
    cache: 'no-store',
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
}

export function followUser(token: string, userId: string | number) {
  return apiFetch<FollowResponse>('/api/social/follow', {
    method: 'POST',
    headers: { ...clientHeaders(), ...authHeader(token) },
    body: jsonBody({ userId }),
  });
}

export function unfollowUser(token: string, userId: string | number) {
  return apiFetch<FollowResponse>(`/api/social/follow/${encodeURIComponent(String(userId))}`, {
    method: 'DELETE',
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
}

export function fetchLeaderboard(params: {
  token?: string;
  period: LeaderboardPeriod;
  game: LeaderboardGame;
  room: string;
  board?: LeaderboardBoard;
  sort?: LeaderboardSort;
  onlineOnly?: boolean;
  minPlays?: number;
}) {
  const search = new URLSearchParams({
    currency: 'BRL',
    period: params.period,
    game: params.game,
    room: params.room || 'all',
    limit: '50',
    board: params.board || 'profit',
    sort: params.sort || 'profit',
    onlineOnly: params.onlineOnly ? 'true' : 'false',
  });
  if (typeof params.minPlays === 'number') search.set('minPlays', String(params.minPlays));
  return apiFetch<LeaderboardResponse>(`/api/leaderboard?${search.toString()}`, {
    headers: {
      ...clientHeaders(),
      ...authHeader(params.token),
    },
  });
}

export function fetchPublicProfile(params: { token?: string; userId: string; period: PublicProfilePeriod; limit?: number }) {
  const search = new URLSearchParams({
    period: params.period,
    limit: String(params.limit || 5),
  });
  return apiFetch<PublicProfileResponse>(`/api/users/${encodeURIComponent(params.userId)}/public-profile?${search.toString()}`, {
    cache: 'no-store',
    headers: {
      ...clientHeaders(),
      ...authHeader(params.token),
    },
  });
}
